import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "../layouts/AppLayout";
import { register } from "../services/chatService";
import "./css/Register.css";

const Register = () => {
  const navigate = useNavigate();

  // Form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [gender, setGender] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Captcha state
  const [requireCaptcha, setRequireCaptcha] = useState(true); // always show
  const [captchaToken, setCaptchaToken] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Loading state
  const [loading, setLoading] = useState(false);
  const [slowRegister, setSlowRegister] = useState(false);

  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate("/profile");
  }, [navigate]);

  // Load Turnstile script once
  useEffect(() => {
    if (window.turnstile || document.getElementById("cf-turnstile-script")) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    document.body.appendChild(script);
  }, []);

  // Render CAPTCHA
  useEffect(() => {
    if (
      scriptLoaded &&
      requireCaptcha &&
      captchaRef.current &&
      window.turnstile &&
      siteKey &&
      widgetIdRef.current === null
    ) {
      widgetIdRef.current = window.turnstile.render(captchaRef.current, {
        sitekey: siteKey,
        callback: (token) => setCaptchaToken(token),
        "error-callback": () => {
          setError("CAPTCHA failed. Please refresh and try again.");
          setCaptchaToken(null);
        },
      });
    }
  }, [scriptLoaded, requireCaptcha, siteKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    setLoading(true);
    setSlowRegister(false);

    const slowTimer = setTimeout(() => setSlowRegister(true), 5000);

    try {
      const res = await register(username, password, gender, captchaToken);
      localStorage.setItem("token", res.data.token);

      // Reset CAPTCHA after success
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      setCaptchaToken(null);
      setRequireCaptcha(true); // always required

      navigate("/profile");
    } catch (err) {
      const data = err.response?.data;

      if (data?.requireCaptcha) {
        setRequireCaptcha(true);
        setCaptchaToken(null);
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.reset(widgetIdRef.current);
        }
      }

      if (Array.isArray(data?.errors)) {
        const errorsObj = {};
        data.errors.forEach((e) => (errorsObj[e.field] = e.message));
        setFieldErrors(errorsObj);
      } else {
        setError(data?.message || "Registration failed. Please try again.");
      }
    } finally {
      clearTimeout(slowTimer);
      setLoading(false);
      setSlowRegister(false);
    }
  };

  return (
    <AppLayout>
      <div className="register-hero">
        <div className="container">
          <h2>Create Your Account</h2>
          <p className="subtext">
            Already have an account?{" "}
            <Link to="/login" className="login-link">
              Login
            </Link>
          </p>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
            {fieldErrors.username && (
              <p className="error">{fieldErrors.username}</p>
            )}

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            {fieldErrors.password && (
              <p className="error">{fieldErrors.password}</p>
            )}

            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              required
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {fieldErrors.gender && (
              <p className="error">{fieldErrors.gender}</p>
            )}

            {/* CAPTCHA always rendered */}
            <div
              ref={captchaRef}
              className="captcha-box"
              style={{ margin: "15px 0" }}
            />

            <button
              type="submit"
              className="btn register-btn"
              disabled={loading || !captchaToken}
            >
              {loading ? (
                <span>
                  <span className="spinner"></span>
                  {slowRegister
                    ? "Wait, Waking up server..."
                    : "Creating account..."}
                </span>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          {error && <p className="error general-error">{error}</p>}
        </div>
      </div>
    </AppLayout>
  );
};

export default Register;
